/**
 * The single source for how mockd describes itself (docs/brand.md → Canonical strings).
 *
 * The web app's `index.html` meta and PWA manifest are injected from here by
 * `vite.config.ts`, and per-page titles go through `pageTitle()`. Surfaces that can't
 * import — README.md, docs/VISION.md, the docs site's Markdown — repeat the text, and
 * `brandCopy.test.ts` fails if they drift.
 *
 * Kept import-free and to erasable TypeScript syntax, like tokens.ts, so config
 * loaders can import it by file path.
 */

export const PRODUCT_NAME = 'mockd';

/** Headline: the landing hero, README, docs home, docs/VISION.md north star. */
export const TAGLINE = 'The fastest path from nothing to a working mock API.';

/** README, docs intro, OG/Twitter description, PWA manifest. */
export const ONE_LINER =
  'Mock API endpoints with a live URL and real-time request logs — no server code, no signup.';

/** `<meta name="description">` for the web app. */
export const META_DESCRIPTION =
  'Create mock API endpoints at your-project.mockd.sh and watch every request arrive in real time. No server code, no signup.';

/** Between a page name and the product name, here and in the docs site's `titleDelimiter`. */
export const TITLE_SEPARATOR = ' — ';

/**
 * `<Page> — mockd`. Page names are sentence case, except the legal documents' own
 * names ("Privacy Policy", "Terms of Service").
 */
export function pageTitle(page: string): string {
  return `${page}${TITLE_SEPARATOR}${PRODUCT_NAME}`;
}

/**
 * The home page title, and the default for every route that doesn't set its own. The
 * one title that leads with the name.
 */
export const HOME_TITLE = `${PRODUCT_NAME}${TITLE_SEPARATOR}Mock API endpoints with live request logs`;

/** The `/pricing` headline, and its social card's. */
export const PRICING_HEADLINE = 'Free to start. Upgrade for teams and higher limits.';

/** Open Graph / Twitter card image size, in px. */
export const SOCIAL_CARD_SIZE = { width: 1200, height: 630 } as const;

export interface SocialCard {
  /** The card's headline. */
  headline: string;
  /** Where the card points, printed on it. */
  host: string;
  /** Absolute URL the PNG is served from. */
  url: string;
  alt: string;
}

const socialCard = (headline: string, host: string, url: string): SocialCard => ({
  headline,
  host,
  url,
  alt: `${PRODUCT_NAME}${TITLE_SEPARATOR}${headline}`,
});

/**
 * Link-preview images (docs/brand.md → Social cards). The PNGs are generated by
 * `pnpm --filter @mockd/web generate:brand` and committed; `url` is where each is
 * deployed. `home` is the default for every mockd.sh route without its own.
 */
export const SOCIAL_CARDS = {
  home: socialCard(TAGLINE, 'mockd.sh', 'https://mockd.sh/brand/og-home.png'),
  pricing: socialCard(PRICING_HEADLINE, 'mockd.sh/pricing', 'https://mockd.sh/brand/og-pricing.png'),
  docs: socialCard('Documentation', 'docs.mockd.sh', 'https://docs.mockd.sh/og.png'),
} as const;

export type SocialImageMeta = { property: string; content: string } | { name: string; content: string };

/** The `og:image*` and `twitter:image*` meta tags for `card`, as attribute objects. */
export function socialImageMeta(card: SocialCard): SocialImageMeta[] {
  return [
    { property: 'og:image', content: card.url },
    { property: 'og:image:width', content: String(SOCIAL_CARD_SIZE.width) },
    { property: 'og:image:height', content: String(SOCIAL_CARD_SIZE.height) },
    { property: 'og:image:alt', content: card.alt },
    { name: 'twitter:image', content: card.url },
    { name: 'twitter:image:alt', content: card.alt },
  ];
}
